import { mapValues } from "lodash";
import validator from "trezor-address-validator";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Option } from "space-monad";
import {
  AnySchema,
  array,
  boolean,
  date,
  mixed,
  number,
  object,
  string,
} from "yup";
import {
  countries,
  useIdentityAttachmentType,
  processingStatus,
  useAddressAttachmentType,
  IdentityAttachmentType,
} from ".";
import {
  AllowedBlockchains,
  ProductionBlockchains,
  validationMap,
} from "../../chain/models/internal";
import {
  FundsTypeEnum,
  ReportingOptionEnum,
  SocialMediaEnum,
  TokenLifecycleEnum,
  TypeOfFraudEnum,
} from "../../crypto/models/internal";
import { StatusEnum } from "../../users/models/internal";
import { DeclineCode, ServiceType } from "../../verification/models/internal";

function createField<T>(useName: () => string, useSchema: () => T) {
  return {
    useName,
    useSchema,
  };
}

const specialChar = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
const digitChar = /[0-9]/;
const uppercaseChar = /[A-Z]/;

export const chain = createField(
  () => {
    const { t } = useTranslation("fields");
    return useMemo(() => t("chain"), [t]);
  },
  () => {
    const { t } = useTranslation("fields");
    return useMemo(
      () =>
        mixed<AllowedBlockchains>()
          .oneOf(
            [null, ...ProductionBlockchains.keys],
            t("chain-must-be-from-the-list")
          )
          .label(t("chain")),
      [t]
    );
  }
);

export const address = createField(
  () => {
    const { t } = useTranslation("fields");
    return useMemo(() => t("wallet-address"), [t]);
  },
  () => {
    const { t } = useTranslation("fields");

    return useMemo(
      () =>
        string()
          .test(
            "address-valid",
            t("address-is-not-valid"),
            (value: string | undefined, context) => {
              if (value === undefined) return true;
              return Option(context.parent)
                .map(x => x.blockchain || x.chain)
                .fold(
                  () => true,
                  curr => {
                    if (["SOL", "AVAX", "MNT"].includes(curr)) return true;
                    const newValidator =
                      validationMap[curr as AllowedBlockchains];
                    return validator.validate(
                      value,
                      (newValidator || curr).toLowerCase()
                    );
                  }
                );
            }
          )
          .label(t("wallet-address")),
      [t]
    );
  }
);

export const validationFields = {
  email: {
    useName: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("e-mail"), [t]);
    },
    useSchema: () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .email(t("field-is-invalid"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("e-mail"),
              })
            )
            .label(t("e-mail")),
        [t]
      );
    },
  },
  merchantId: {
    useName: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("merchant-id"), [t]);
    },
    useSchema: () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("merchant-id"),
              })
            )
            .label(t("merchant-id")),
        [t]
      );
    },
  },

  expiresAt: {
    useName: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("expires-at"), [t]);
    },
    useSchema: () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => date().typeError(t("field-is-invalid")).label(t("expires-at")),
        [t]
      );
    },
  },
  callbackUrl: {
    useName: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("callback-url"), [t]);
    },
    useSchema: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => string().label(t("callback-url")), [t]);
    },
  },
  formBaseUrl: {
    useName: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("form-base-url"), [t]);
    },
    useSchema: () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("form-base-url"),
              })
            )
            .label(t("form-base-url")),
        [t]
      );
    },
  },
  sendEmails: {
    useName: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("send-emails"), [t]);
    },
    useSchema: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => boolean().label(t("send-emails")), [t]);
    },
  },
  receiveMarketingMaterial: {
    useName: () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("receive-marketing-material"), [t]);
    },
    useSchema: () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          boolean()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("receive-marketing-material"),
              })
            )
            .label(t("receive-marketing-material")),
        [t]
      );
    },
  },
  password: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("password"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .min(
              8,
              t("field-min-length", {
                ns: "errors",
                field: t("password"),
                min: 8,
              })
            )
            .max(
              30,
              t("field-max-length", {
                ns: "errors",
                max: 30,
                field: t("password"),
              })
            )
            .required(
              t("required-field", {
                ns: "errors",
                field: t("password"),
              })
            )
            .test(
              "password-special",
              t("password-requires-at-least-1-special-character"),
              value => value === undefined || specialChar.test(value)
            )
            .test(
              "password-digit",
              t("password-requires-at-least-1-digit-character"),
              value => value === undefined || digitChar.test(value)
            )
            .test(
              "password-uppercase",
              t("password-requires-at-least-1-uppercase-character"),
              value => value === undefined || uppercaseChar.test(value)
            )
            .label(t("password")),
        [t]
      );
    }
  ),
  confirmPassword: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("confirm-password"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("confirm-password"),
              })
            )
            .test(
              "password-not-matching",
              t("passwords-do-not-match"),
              (value: string | undefined, context) =>
                value === context.parent.password ||
                value === context.parent.newPassword
            ),
        [t]
      );
    }
  ),
  sourceWallet: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("source-wallet"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      const addressSchemaFn = address.useSchema();
      const addressSchema = useMemo(
        () =>
          addressSchemaFn.required(
            t("required-field", {
              ns: "errors",
              field: t("wallett-address"),
            })
          ),
        [addressSchemaFn]
      );
      const chainSchemaFn = chain.useSchema();
      const chainSchema = useMemo(
        () =>
          chainSchemaFn.required(
            t("required-field", {
              ns: "errors",
              field: t("chain"),
            })
          ),
        [chainSchemaFn]
      );

      return useMemo(
        () =>
          object({
            address: addressSchema,
            chain: chainSchema,
          }).required(
            t("required-field", {
              ns: "errors",
              field: t("source-wallet"),
            })
          ),
        [addressSchema, chainSchema]
      );
    }
  ),
  destinationWallet: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("destination-wallet"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      const addressSchemaFn = address.useSchema();
      const addressSchema = useMemo(() => addressSchemaFn.notRequired(), [
        addressSchemaFn,
      ]);
      const chainSchemaFn = chain.useSchema();
      const chainSchema = useMemo(() => chainSchemaFn.notRequired(), [
        chainSchemaFn,
      ]);

      return useMemo(
        () =>
          object({
            address: addressSchema,
            chain: chainSchema,
          }).required(
            t("required-field", {
              ns: "errors",
              field: t("destination-wallet"),
            })
          ),
        [addressSchema, chainSchema]
      );
    }
  ),
  chain: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("chain"), [t]);
    },

    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<AllowedBlockchains>()
            .oneOf(
              [null, ...ProductionBlockchains.keys],
              t("chain-must-be-from-the-list")
            )
            .label(t("chain")),
        [t]
      );
    }
  ),
  provider: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("provider"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("provider"),
              })
            )
            .label(t("provider")),
        [t]
      );
    }
  ),
  otp: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("one-time-password"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("provider"),
              })
            )
            .label(t("one-time-password")),
        [t]
      );
    }
  ),
  firstName: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("first-name"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("first-name"),
              })
            )
            .max(
              30,
              t("field-max-length", {
                ns: "errors",
                max: 30,
              })
            )
            .label(t("first-name")),
        [t]
      );
    }
  ),
  middleName: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("middle-name"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .notRequired()
            .max(
              30,
              t("field-max-length", {
                ns: "errors",
                max: 30,
              })
            )
            .label(t("middle-name")),
        [t]
      );
    }
  ),
  lastName: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("last-name"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("last-name"),
              })
            )
            .max(
              30,
              t("field-max-length", {
                ns: "errors",
                max: 30,
              })
            )
            .label(t("last-name")),
        [t]
      );
    }
  ),
  dateOfBirth: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("date-of-birth"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          date()
            .typeError(t("field-is-invalid"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("date-of-birth"),
              })
            )
            .max(new Date(), t("date-must-be-in-the-past"))
            .label(t("date-of-birth")),
        [t]
      );
    }
  ),
  createdDate: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("date-created"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          date()
            .typeError(t("field-is-invalid"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("date-created"),
              })
            )
            .max(new Date(), t("date-must-be-in-the-past"))
            .label(t("date-created")),
        [t]
      );
    }
  ),
  companyIncorporationDate: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("company-incorporation-date"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          date()
            .typeError(t("field-is-invalid"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("company-incorporation-date"),
              })
            )
            .max(new Date(), t("date-must-be-in-the-past"))
            .label(t("company-incorporation-date")),
        [t]
      );
    }
  ),
  documents: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("document"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      const addressAttachmentType = useAddressAttachmentType();
      const identityAttachmentType = useIdentityAttachmentType();

      return useMemo(
        () =>
          array().of(
            object().shape({
              fileName: string()
                .required(
                  t("required-field", {
                    ns: "errors",
                    field: t("file-name"),
                  })
                )
                .label(t("file-name")),
              id: string(),
              type: mixed<
                IdentityAttachmentType | typeof addressAttachmentType.T
              >()
                .oneOf([
                  ...identityAttachmentType.keys,
                  ...addressAttachmentType.keys,
                ])
                .label(t("type")),
              uploadedAt: date()
                .typeError(t("field-is-invalid"))
                .label("uploaded-at"),
            })
          ),
        [t]
      );
    }
  ),
  ongoingBackgroundChecks: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("ongoing-background-checks"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => boolean().notRequired().label(t("ongoing-background-checks")),
        [t]
      );
    }
  ),
  ongoingCheck: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("ongoing-check"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => boolean().notRequired().label(t("ongoing-check")), [
        t,
      ]);
    }
  ),
  pep: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("politically-exposed-person"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => boolean().notRequired().label(t("politically-exposed-person")),
        [t]
      );
    }
  ),
  usPerson: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("from-the-usa"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => boolean().notRequired().label(t("from-the-usa")), [
        t,
      ]);
    }
  ),
  finalDeclaration: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("final-declaration"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => boolean().notRequired().label(t("final-declaration")),
        [t]
      );
    }
  ),
  resideSanctionedCountry: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("reside-sanctioned-country"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => boolean().notRequired().label(t("reside-sanctioned-country")),
        [t]
      );
    }
  ),
  declarationStatements: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("declaration-statements"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => boolean().notRequired().label(t("declaration-statements")),
        [t]
      );
    }
  ),
  whitepaperRead: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("whitepaper-read"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => boolean().notRequired().label(t("whitepaper-read")),
        [t]
      );
    }
  ),
  requireHelp: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("require-help"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => boolean().notRequired().label(t("require-help")), [
        t,
      ]);
    }
  ),
  processingStatus: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("processing-status"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof processingStatus.T>()
            .oneOf(processingStatus.keys)
            .label(t("processing-status")),
        [t]
      );
    }
  ),
  pledge: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("pledge"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          object({
            amount: number().nullable().label(t("amount")),
            currency: string().nullable().label(t("currency")),
          })
            .notRequired()
            .default(undefined)
            .meta({
              label: t("pledge"),
            }),
        [t]
      );
    }
  ),
  companyName: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("company-name"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("company-name"),
              })
            )
            .label(t("company-name")),
        [t]
      );
    }
  ),
  identityDocument: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("identity-document"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          object()
            .shape({
              expiryDate: date().typeError(t("field-is-invalid")).notRequired(),
              issueDate: date().typeError(t("field-is-invalid")).notRequired(),
              number: string().notRequired(),
            })
            .notRequired()
            .label(t("identity-document")),
        [t]
      );
    }
  ),
  address: address,
  locatedIn: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("located-in"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof countries.T>()
            .oneOf(countries.keys, t("country-must-be-one-of-list"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("located-in"),
              })
            )
            .label(t("located-in")),
        [t]
      );
    }
  ),
  nationality: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("nationality"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof countries.T>()
            .oneOf(countries.keys, t("country-must-be-one-of-list"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("nationality"),
              })
            )
            .label(t("nationality")),
        [t]
      );
    }
  ),
  countryOfResidence: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("country-of-residence"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof countries.T>()
            .oneOf(countries.keys, t("country-must-be-one-of-list"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("country-of-residence"),
              })
            )
            .label(t("country-of-residence")),
        [t]
      );
    }
  ),
  numberOfEntries: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("number-of-entries"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          number()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("number-of-entries"),
              })
            )
            .label(t("number-of-entries")),
        [t]
      );
    }
  ),
  userReferenceId: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("user-reference-id"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => string().notRequired().label(t("user-reference-id")),
        [t]
      );
    }
  ),
  applicationPin: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("application-pin"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => string().notRequired().label(t("application-pin")), [
        t,
      ]);
    }
  ),
  fraudCheckId: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("fraud-check-id"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("fraud-check-id"),
              })
            )
            .label(t("fraud-check-id")),
        [t]
      );
    }
  ),
  pin: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("pin"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("pin"),
              })
            )
            .max(
              4,
              t("field-max-length", {
                ns: "errors",
                max: 4,
              })
            )
            .min(4)
            .label(t("pin")),
        [t]
      );
    }
  ),
  wallets: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("eth-wallet-address"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          array()
            .of(
              object().shape({
                address: string().label(t("address")),
                chain: string().label(t("chain")),
              })
            )
            .notRequired()
            .label(t("eth-wallet-address")), // TODO use lib to validate
        [t]
      );
    }
  ),
  transactions: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("transactions"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          array()
            .of(
              object().shape({
                transactionDate: date()
                  .typeError(t("field-is-invalid"))
                  .max(new Date(), t("date-must-be-in-the-past"))
                  .label(t("transaction-date")),
                reference: string()
                  .required(
                    t("required-field", {
                      ns: "errors",
                      field: t("transaction-reference"),
                    })
                  )
                  .label(t("transaction-reference")),
              })
            )
            .label(t("transactions")), // TODO use lib to validate
        [t]
      );
    }
  ),
  transactionHash: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("transaction-hash"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("transaction-hash"),
              })
            )
            .label(t("transaction-hash")),
        [t]
      );
    }
  ),
  socialMedia: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("social-media"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          array()
            .of(
              object().shape({
                type: mixed<typeof SocialMediaEnum.T>()
                  .oneOf(SocialMediaEnum.keys)
                  .label(t("type")),
                value: string().label(t("value")),
              })
            )
            .label(t("social-media")), // TODO use lib to validate
        [t]
      );
    }
  ),
  attachments: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("attachments"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          array()
            .of(
              object().shape({
                id: string().label(t("ID")),
                attachedDate: string().label(t("attached-date")),
              })
            )
            .notRequired()
            .label(t("attachments")), // TODO use lib to validate
        [t]
      );
    }
  ),

  id: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("id"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("id"),
              })
            )
            .label(t("id")),
        [t]
      );
    }
  ),
  streetAddress: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("street-address"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .max(
              100,
              t("field-max-length", {
                ns: "errors",
                max: 100,
              })
            )
            .notRequired()
            .label(t("street-address")),
        [t]
      );
    }
  ),
  city: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("city"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .max(
              50,
              t("field-max-length", {
                ns: "errors",
                max: 50,
              })
            )
            .required(
              t("required-field", {
                ns: "errors",
                field: t("city"),
              })
            )
            .label(t("city")),
        [t]
      );
    }
  ),
  zip: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("zip"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .max(
              25,
              t("field-max-length", {
                ns: "errors",
                max: 25,
              })
            )
            .required(
              t("required-field", {
                ns: "errors",
                field: t("zip"),
              })
            )
            .label(t("zip")),
        [t]
      );
    }
  ),
  language: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("language"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("language"),
              })
            )
            .label(t("language")),
        [t]
      );
    }
  ),
  state: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("state"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .max(
              25,
              t("field-max-length", {
                ns: "errors",
                max: 25,
                field: t("state"),
              })
            )
            .notRequired()
            .label(t("state")),
        [t]
      );
    }
  ),
  street: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("street"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .max(
              100,
              t("field-max-length", {
                ns: "errors",
                max: 100,
              })
            )
            .notRequired()
            .label(t("street")),
        [t]
      );
    }
  ),

  friendlyName: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("friendly-name"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("friendly-name"),
              })
            )
            .label(t("friendly-name")),
        [t]
      );
    }
  ),
  serviceDetails: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("service-details"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          array()
            .of(
              object({
                service: mixed<typeof ServiceType.T>()
                  .oneOf(
                    ServiceType.keys,
                    t("service-type-must-be-one-of-list")
                  )
                  .required(),
                details: object({
                  status: mixed().oneOf(["VERIFIED", "DECLINED"]),
                  declineCodes: array().of(
                    object({
                      code: mixed<typeof DeclineCode.T>().oneOf(
                        DeclineCode.keys,
                        "decline-code-must-be-one-of-the-list"
                      ),
                      message: string(),
                    })
                  ),
                }),
              })
            )
            .label(t("service-details")),
        [t]
      );
    }
  ),
  phoneCountryCode: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("phone-country-code"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .notRequired()
            // .matches(/(00|[+])[0-9]+/)
            .max(
              10,
              t("field-max-length", {
                ns: "errors",
                max: 10,
              })
            )
            .label(t("phone-country-code")),
        [t]
      );
    }
  ),
  phone: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("phone"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            // .matches(/[0-9]+/)
            .max(
              15,
              t("field-max-length", {
                ns: "errors",
                max: 15,
              })
            )
            .required(
              t("required-field", {
                ns: "errors",
                field: t("phone"),
              })
            )
            .label(t("phone")),
        [t]
      );
    }
  ),
  country: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("country"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof countries.T>()
            .oneOf(countries.keys, t("country-must-be-one-of-list"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("country"),
              })
            )
            .label(t("country")),
        [t]
      );
    }
  ),
  kycStatus: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("kyc-status"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof StatusEnum.T>()
            .oneOf(StatusEnum.keys)
            .label(t("kyc-status")),
        [t]
      );
    }
  ),
  kybStatus: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("kyb-status"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof StatusEnum.T>()
            .oneOf(StatusEnum.keys)
            .label(t("kyb-status")),
        [t]
      );
    }
  ),
  amlStatus: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("aml-status"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof StatusEnum.T>()
            .oneOf(StatusEnum.keys)
            .label(t("aml-status")),
        [t]
      );
    }
  ),
  reportType: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("report-type"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof ReportingOptionEnum.T>()
            .oneOf(ReportingOptionEnum.keys)
            .label(t("report-type")),
        [t]
      );
    }
  ),
  blockchain: chain,
  fraudType: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("fraud-type"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof TypeOfFraudEnum.T>()
            .oneOf(TypeOfFraudEnum.keys)
            .label(t("fraud-type")),
        [t]
      );
    }
  ),
  tokenLifecycle: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("token-lifecycle"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof TokenLifecycleEnum.T>()
            .oneOf(TokenLifecycleEnum.keys)
            .label(t("token-lifecycle")),
        [t]
      );
    }
  ),
  fundsType: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("funds-type"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed<typeof FundsTypeEnum.T>()
            .oneOf(FundsTypeEnum.keys)
            .label(t("funds-type")),
        [t]
      );
    }
  ),
  declineReason: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("decline-reason"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .notRequired()
            .max(
              50,
              t("field-max-length", {
                ns: "errors",
                max: 50,
              })
            )
            .label(t("decline-reason")),
        [t]
      );
    }
  ),
  description: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("description"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("description"),
              })
            )
            .max(
              50,
              t("field-max-length", {
                ns: "errors",
                max: 50,
              })
            )
            .label(t("description")),
        [t]
      );
    }
  ),
  fraudCurrency: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("fraud-currency"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("fraud-currency"),
              })
            )
            .max(
              50,
              t("field-max-length", {
                ns: "errors",
                max: 50,
              })
            )
            .label(t("fraud-currency")),
        [t]
      );
    }
  ),
  fraudAmount: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("amount"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          number()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("amount"),
              })
            )
            .label(t("amount")),
        [t]
      );
    }
  ),
  action: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("action"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("action"),
              })
            )
            .max(
              50,
              t("field-max-length", {
                ns: "errors",
                max: 50,
              })
            )
            .label(t("action")),
        [t]
      );
    }
  ),
  identityAttachmentType: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("identity-attachment-type"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      const identityAttachmentType = useIdentityAttachmentType();
      return useMemo(
        () =>
          mixed<IdentityAttachmentType>()
            .oneOf(identityAttachmentType.keys)
            .label(t("identity-attachment-type")),
        [t]
      );
    }
  ),
  identityAttachment: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("identity-attachment"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => mixed().label(t("identity-attachment")), [t]);
    }
  ),
  evidence: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("evidence"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => mixed().notRequired().label(t("evidence")), [t]);
    }
  ),
  attachment: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("attachment"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("attachment"),
              })
            )
            .label(t("attachment")),
        [t]
      );
    }
  ),
  additionalIdentityAttachmentType: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("additional-identity-attachment-type"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      const identityAttachmentType = useIdentityAttachmentType();
      return useMemo(
        () =>
          mixed<IdentityAttachmentType>()
            .oneOf(identityAttachmentType.keys)
            .notRequired()
            .label(t("additional-identity-attachment-type")),
        [t]
      );
    }
  ),
  additionalIdentityAttachment: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("additional-identity-attachment"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed()
            .when("additionalIdentityAttachmentType", {
              is: !undefined,
              then: mixed().required(
                t("required-field", {
                  ns: "errors",
                  field: t("additional-identity-attachment"),
                })
              ), //TODO check for the others
            })
            .label(t("additional-identity-attachment")),
        [t]
      );
    }
  ),
  identityDocumentNumber: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("identity-document-number"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () => string().notRequired().label(t("identity-document-number")),
        [t]
      );
    }
  ),
  identityDocumentIssueDate: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("identity-document-issue-date"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          date()
            .typeError(t("field-is-invalid"))
            .notRequired()
            .max(new Date(), t("date-must-be-in-the-past"))
            .label(t("identity-document-issue-date")),
        [t]
      );
    }
  ),
  identityDocumentExpiryDate: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("identity-document-expiry-date"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          date()
            .typeError(t("field-is-invalid"))
            .notRequired()
            .min(new Date(), "date-must-be-in-the-future")
            .label(t("identity-document-expiry-date")),
        [t]
      );
    }
  ),
  addressAttachmentType: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("address-attachment-type"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      const addressAttachmentType = useAddressAttachmentType();
      return useMemo(
        () =>
          mixed<typeof addressAttachmentType.T>()
            .oneOf(addressAttachmentType.keys)
            .required(
              t("required-field", {
                ns: "errors",
                field: t("address-attachment-type"),
              })
            )
            .label(t("address-attachment-type")),
        [t]
      );
    }
  ),
  addressAttachment: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("address-attachment"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed()
            .when("addressAttachmentType", {
              is: !undefined,
              then: mixed().required(
                t("required-field", {
                  ns: "errors",
                  field: t("address-attachment"),
                })
              ), //TODO check for the others
            })
            .test({
              test: fileList => fileList.length > 0,
              message: t("address-attachment-required"),
            })
            .required()
            .label(t("address-attachment")),
        [t]
      );
    }
  ),
  otherFraudDescription: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("fraud-description"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          mixed()
            .when("fraudType", {
              is: "OTHER",
              then: string().required(
                t("required-field", {
                  ns: "errors",
                  field: t("fraud-description"),
                })
              ),
            })
            .label(t("fraud-description")),
        [t]
      );
    }
  ),
  faceAttachment: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("face-attachment"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => mixed().notRequired().label(t("face-attachment")), [
        t,
      ]);
    }
  ),
  backgroundChecks: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("background-checks"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          object()
            .shape({
              ongoingCheck: boolean(),
            })
            .notRequired()
            .label(t("background-checks")),
        [t]
      );
    }
  ),
  referrer: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("referrer"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => string().notRequired().label(t("referrer")), [t]);
    }
  ),
  note: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("note"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => string().notRequired().label(t("note")), [t]);
    }
  ),
  companyRegistrationNumber: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("registration-number"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          string()
            .required(
              t("required-field", {
                ns: "errors",
                field: t("registration-number"),
              })
            )
            .label(t("registration-number")),
        [t]
      );
    }
  ),
  companyJurisdictionCountryCode: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("jurisdiction-country"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => string().label(t("jurisdiction-country")), [t]);
    }
  ),
  businessAml: createField(
    () => {
      const { t } = useTranslation("fields");
      return useMemo(() => t("business-aml"), [t]);
    },
    () => {
      const { t } = useTranslation("fields");
      return useMemo(
        () =>
          object()
            .shape({
              ongoingCheck: boolean().notRequired(),
              callbackUrl: string().notRequired(),
            })
            .notRequired()
            .label(t("business-aml")),
        [t]
      );
    }
  ),
};
export function getFields<K extends string>(
  f: Record<
    K,
    {
      schema: AnySchema<any>;
      name: string;
    }
  >
) {
  return {
    validation: object(mapValues(f, val => val.schema)),
    labels: mapValues(f, val => val.name),
  };
}
